import { useState, useRef, useEffect, type ReactNode, type KeyboardEvent, type MouseEvent as ReactMouseEvent } from "react";
import { Check, ChevronDown } from "lucide-react";

export interface CustomSelectOption<T extends string | number = string> {
  value: T;
  label: string;
  sublabel?: string;
  flag?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}

export function MarketplaceFlag({
  marketplace,
  className = "",
}: {
  marketplace: string;
  className?: string;
}) {
  const code = marketplace.toUpperCase();

  if (code === "UK" || code === "GB") {
    return (
      <svg className={`flag-svg ${className}`} width="18" height="13" viewBox="0 0 20 15" aria-label="UK Flag">
        <defs>
          <clipPath id="uk-flag-clip">
            <rect width="20" height="15" rx="2" />
          </clipPath>
        </defs>
        <g clipPath="url(#uk-flag-clip)">
          <rect width="20" height="15" fill="#012169" />
          <path d="M0 0 L20 15 M20 0 L0 15" stroke="#ffffff" strokeWidth="2.8" />
          <path d="M0 0 L20 15 M20 0 L0 15" stroke="#C8102E" strokeWidth="1.6" />
          <path d="M10 0 V15 M0 7.5 H20" stroke="#ffffff" strokeWidth="4.8" />
          <path d="M10 0 V15 M0 7.5 H20" stroke="#C8102E" strokeWidth="2.8" />
        </g>
      </svg>
    );
  }

  if (code === "FR") {
    return (
      <svg className={`flag-svg ${className}`} width="18" height="13" viewBox="0 0 20 15" aria-label="FR Flag">
        <defs>
          <clipPath id="fr-flag-clip">
            <rect width="20" height="15" rx="2" />
          </clipPath>
        </defs>
        <g clipPath="url(#fr-flag-clip)">
          <rect width="6.67" height="15" fill="#002395" />
          <rect x="6.67" width="6.67" height="15" fill="#ffffff" />
          <rect x="13.34" width="6.67" height="15" fill="#ED2939" />
        </g>
      </svg>
    );
  }

  if (code === "IT") {
    return (
      <svg className={`flag-svg ${className}`} width="18" height="13" viewBox="0 0 20 15" aria-label="IT Flag">
        <defs>
          <clipPath id="it-flag-clip">
            <rect width="20" height="15" rx="2" />
          </clipPath>
        </defs>
        <g clipPath="url(#it-flag-clip)">
          <rect width="6.67" height="15" fill="#009246" />
          <rect x="6.67" width="6.67" height="15" fill="#ffffff" />
          <rect x="13.34" width="6.67" height="15" fill="#CE2B37" />
        </g>
      </svg>
    );
  }

  if (code === "ES") {
    return (
      <svg className={`flag-svg ${className}`} width="18" height="13" viewBox="0 0 20 15" aria-label="ES Flag">
        <defs>
          <clipPath id="es-flag-clip">
            <rect width="20" height="15" rx="2" />
          </clipPath>
        </defs>
        <g clipPath="url(#es-flag-clip)">
          <rect width="20" height="15" fill="#AA151B" />
          <rect y="3.75" width="20" height="7.5" fill="#F1BF00" />
        </g>
      </svg>
    );
  }

  if (code === "DE") {
    return (
      <svg className={`flag-svg ${className}`} width="18" height="13" viewBox="0 0 20 15" aria-label="DE Flag">
        <defs>
          <clipPath id="de-flag-clip">
            <rect width="20" height="15" rx="2" />
          </clipPath>
        </defs>
        <g clipPath="url(#de-flag-clip)">
          <rect width="20" height="5" fill="#1e293b" />
          <rect y="5" width="20" height="5" fill="#DD0000" />
          <rect y="10" width="20" height="5" fill="#FFCE00" />
        </g>
      </svg>
    );
  }

  if (code === "US") {
    return (
      <svg className={`flag-svg ${className}`} width="18" height="13" viewBox="0 0 20 15" aria-label="US Flag">
        <defs>
          <clipPath id="us-flag-clip">
            <rect width="20" height="15" rx="2" />
          </clipPath>
        </defs>
        <g clipPath="url(#us-flag-clip)">
          <rect width="20" height="15" fill="#B22234" />
          <path d="M0 2.5 H20 M0 5.5 H20 M0 8.5 H20 M0 11.5 H20" stroke="#ffffff" strokeWidth="1.2" />
          <rect width="9" height="8" fill="#3C3B6E" />
        </g>
      </svg>
    );
  }

  return <span className="select-flag-text">🌐</span>;
}

export function getMarketplaceFlag(marketplace: string): ReactNode {
  return <MarketplaceFlag marketplace={marketplace} />;
}

interface CustomSelectProps<T extends string | number> {
  value: T;
  onChange: (value: T) => void;
  options: CustomSelectOption<T>[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  "aria-label"?: string;
  title?: string;
}

export function CustomSelect<T extends string | number>({
  value,
  onChange,
  options,
  placeholder = "请选择",
  className = "",
  disabled = false,
  "aria-label": ariaLabel,
  title,
}: CustomSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [placement, setPlacement] = useState<"bottom" | "top">("bottom");
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  const updatePlacement = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const dialogEl = containerRef.current.closest(".dialog, [role='dialog']");
      let spaceBelow = window.innerHeight - rect.bottom;
      let spaceAbove = rect.top;

      if (dialogEl) {
        const dialogRect = dialogEl.getBoundingClientRect();
        spaceBelow = Math.min(spaceBelow, dialogRect.bottom - rect.bottom);
        spaceAbove = Math.min(spaceAbove, rect.top - dialogRect.top);
      }

      if (spaceBelow < 220 && spaceAbove > 140) {
        setPlacement("top");
      } else {
        setPlacement("bottom");
      }
    }
  };

  const handleToggle = (e?: ReactMouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (disabled) return;
    if (!isOpen) {
      updatePlacement();
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: globalThis.MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      e.preventDefault();
      e.stopPropagation();
      handleToggle();
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(false);
    }
  };

  const handleSelect = (e: ReactMouseEvent, opt: CustomSelectOption<T>) => {
    e.preventDefault();
    e.stopPropagation();
    if (opt.disabled) return;
    onChange(opt.value);
    setIsOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={`custom-select-container ${isOpen ? "open" : ""} ${disabled ? "disabled" : ""} ${className}`}
      title={title}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="custom-select-trigger"
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel || title || "选择项"}
      >
        <span className="custom-select-value">
          {selectedOption ? (
            <>
              {selectedOption.flag && <span className="select-flag">{selectedOption.flag}</span>}
              {selectedOption.icon && <span className="select-icon-box" aria-hidden="true">{selectedOption.icon}</span>}
              <strong className="select-primary-label">{selectedOption.label}</strong>
              {selectedOption.sublabel && (
                <span className="select-sublabel">· {selectedOption.sublabel}</span>
              )}
            </>
          ) : (
            <span className="select-placeholder">{placeholder}</span>
          )}
        </span>
        <ChevronDown size={14} className={`select-chevron ${isOpen ? "rotate" : ""}`} aria-hidden="true" />
      </button>

      {isOpen && (
        <div
          className={`custom-select-dropdown placement-${placement}`}
          role="listbox"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <div
                key={String(opt.value)}
                role="option"
                aria-selected={isSelected}
                className={`custom-select-item ${isSelected ? "selected" : ""} ${opt.disabled ? "disabled" : ""}`}
                onClick={(e) => handleSelect(e, opt)}
              >
                <div className="select-item-content">
                  {opt.flag && <span className="select-flag">{opt.flag}</span>}
                  {opt.icon && <span className="select-icon-box" aria-hidden="true">{opt.icon}</span>}
                  <strong className="select-primary-label">{opt.label}</strong>
                  {opt.sublabel && <span className="select-sublabel">· {opt.sublabel}</span>}
                </div>
                {isSelected && (
                  <Check size={14} className="select-check-icon" aria-hidden="true" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
